import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import StorageIcon from '@material-ui/icons/Storage'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../helpers'

const QUERY_KEY = ['lori-u-data']

interface LeaseRow {
    area_id: number
    unit: string
    bed: string
    folder: string
    lease_id: number | null
    name: string | null
    email: string | null
    status: string | null
    start: string | null
    end: string | null
    is_renewal: boolean
    is_current: boolean
    empty: boolean
    default_selected: boolean
}

interface SeasonRow {
    id: number
    name: string
    current: boolean
    start: string
    end: string
    offer_count: number
    lead_count: number
}

interface LoriUData {
    apartment_id: number
    apartment_name: string
    seed_term: { start: string; end: string }
    demo_password: string
    renewal_residue: { parents: number; children: number }
    rows: LeaseRow[]
    seasons: SeasonRow[]
}

interface BlockedBed {
    unit: string
    bed: string
    reason: string
}

type ConfirmState =
    | { kind: 'residue' }
    | { kind: 'seed' }
    | { kind: 'season'; season: SeasonRow }
    | null

type ManageAction = 'dates' | 'delete' | 'replace' | null

function isCurrentLease(row: LeaseRow): boolean {
    return (
        !row.empty &&
        row.lease_id != null &&
        (row.status || '').toLowerCase() === 'current'
    )
}

function termError(start: string, end: string): string | null {
    if (!start || !end) return 'Choose a start and end date.'
    if (start >= end) return 'Start date must be before the end date.'
    const now = new Date()
    const today = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
    ].join('-')
    if (end <= today) return 'End date must be after today.'
    return null
}

function blockedSuffix(blocked: BlockedBed[] | undefined): string {
    if (!blocked || blocked.length === 0) return ''
    const sample = blocked
        .slice(0, 3)
        .map((bed) => `${bed.unit} ${bed.bed}: ${bed.reason}`)
        .join('; ')
    const extra = blocked.length > 3 ? ` (+${blocked.length - 3} more)` : ''
    return ` ${sample}${extra}`
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    titleIcon: {
        width: 36,
        height: 36,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.primary.main + '14',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
    passwordRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    passwordValue: {
        fontFamily: 'monospace',
        fontSize: '0.95rem',
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        alignItems: 'center',
    },
    seasons: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    seasonRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(1, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
    },
    manageChoices: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    manageOptions: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
    manageOption: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
    },
    manageOptionText: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    manageOptionButton: {
        flexShrink: 0,
    },
    tableWrap: {
        overflow: 'auto',
        maxHeight: 'calc(100vh - 280px)',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
    },
    headerCell: {
        position: 'sticky',
        top: 0,
        backgroundColor: theme.palette.background.paper,
        zIndex: 1,
    },
}))

function actionError(error: unknown): string {
    const data = (error as { response?: { data?: { error?: string } } })
        ?.response?.data
    return data?.error || 'That action failed.'
}

export const LoriUDataPage = () => {
    const classes = useStyles()
    const queryClient = useQueryClient()
    const [search, setSearch] = useState('')
    const [selectedAreas, setSelectedAreas] = useState<number[]>([])
    const [selectedCurrent, setSelectedCurrent] = useState<number[]>([])
    const [termStart, setTermStart] = useState('')
    const [termEnd, setTermEnd] = useState('')
    const [confirm, setConfirm] = useState<ConfirmState>(null)
    const [manageOpen, setManageOpen] = useState(false)
    const [manageAction, setManageAction] = useState<ManageAction>(null)

    const query = useQuery({
        queryKey: QUERY_KEY,
        queryFn: async () => {
            const response = await axiosInstance.get<LoriUData>(
                '/demo-data/lori-u/',
            )
            return response.data
        },
        retry: false,
        staleTime: Infinity,
    })

    const data = query.data

    useEffect(() => {
        if (!data) return
        setTermStart((current) => current || data.seed_term.start)
        setTermEnd((current) => current || data.seed_term.end)
        setSelectedAreas(
            data.rows
                .filter((row) => row.default_selected)
                .map((row) => row.area_id),
        )
        setSelectedCurrent([])
    }, [data])

    const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })

    const residueMutation = useMutation({
        mutationFn: () =>
            axiosInstance.post('/demo-data/lori-u/remove-renewal-residue/'),
        onSuccess: (response) => {
            const body = response.data as {
                deleted_parents: number
                deleted_children: number
            }
            toast.success(
                `Removed ${body.deleted_parents} renewal leases and ${body.deleted_children} child leases.`,
            )
            setConfirm(null)
            refresh()
        },
        onError: (error) => toast.error(actionError(error)),
    })

    const seedMutation = useMutation({
        mutationFn: (areaIds: number[]) =>
            axiosInstance.post('/demo-data/lori-u/seed-current-leases/', {
                area_ids: areaIds,
                start: termStart,
                end: termEnd,
            }),
        onSuccess: (response) => {
            const body = response.data as { created: number }
            toast.success(`Created ${body.created} current leases.`)
            setConfirm(null)
            refresh()
        },
        onError: (error) => toast.error(actionError(error)),
    })

    const closeManage = () => {
        setManageOpen(false)
        setManageAction(null)
    }

    const datesMutation = useMutation({
        mutationFn: (leaseIds: number[]) =>
            axiosInstance.post('/demo-data/lori-u/current-leases/dates/', {
                lease_ids: leaseIds,
                start: termStart,
                end: termEnd,
            }),
        onSuccess: (response) => {
            const body = response.data as { updated: number }
            toast.success(
                `Updated dates on ${body.updated} current leases. Status stays Current.`,
            )
            closeManage()
            refresh()
        },
        onError: (error) => toast.error(actionError(error)),
    })

    const deleteCurrentMutation = useMutation({
        mutationFn: (leaseIds: number[]) =>
            axiosInstance.post('/demo-data/lori-u/current-leases/delete/', {
                lease_ids: leaseIds,
            }),
        onSuccess: (response) => {
            const body = response.data as {
                deleted: number
                blocked: BlockedBed[]
            }
            const message = `Deleted ${body.deleted} current leases.`
            const suffix = blockedSuffix(body.blocked)
            if (suffix) toast.warn(`${message}${suffix}`)
            else toast.success(message)
            closeManage()
            refresh()
        },
        onError: (error) => toast.error(actionError(error)),
    })

    const replaceMutation = useMutation({
        mutationFn: (leaseIds: number[]) =>
            axiosInstance.post('/demo-data/lori-u/current-leases/replace/', {
                lease_ids: leaseIds,
                start: termStart,
                end: termEnd,
            }),
        onSuccess: (response) => {
            const body = response.data as {
                deleted: number
                created: number
                blocked: BlockedBed[]
            }
            const message = `Replaced ${body.deleted} current leases with ${body.created} new ones.`
            const suffix = blockedSuffix(body.blocked)
            if (suffix) toast.warn(`${message}${suffix}`)
            else toast.success(message)
            closeManage()
            refresh()
        },
        onError: (error) => toast.error(actionError(error)),
    })

    const seasonMutation = useMutation({
        mutationFn: (seasonId: number) =>
            axiosInstance.delete(`/demo-data/lori-u/seasons/${seasonId}/`),
        onSuccess: (response) => {
            const body = response.data as {
                deleted_name: string
                deleted_offers: number
            }
            toast.success(
                `Deleted ${body.deleted_name} and ${body.deleted_offers} offers. Leases were kept.`,
            )
            setConfirm(null)
            refresh()
        },
        onError: (error) => toast.error(actionError(error)),
    })

    const busy =
        residueMutation.isLoading ||
        seedMutation.isLoading ||
        seasonMutation.isLoading ||
        datesMutation.isLoading ||
        deleteCurrentMutation.isLoading ||
        replaceMutation.isLoading

    const visibleRows = useMemo(() => {
        const needle = search.trim().toLowerCase()
        if (!data) return []
        if (!needle) return data.rows
        return data.rows.filter((row) =>
            [row.unit, row.bed, row.folder, row.name, row.email, row.status]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(needle)),
        )
    }, [data, search])

    const selectedCount = selectedAreas.length
    const currentCount = selectedCurrent.length
    const datesInvalid = termError(termStart, termEnd)

    const toggleArea = (areaId: number) => {
        setSelectedAreas((current) =>
            current.includes(areaId)
                ? current.filter((id) => id !== areaId)
                : [...current, areaId],
        )
    }

    const toggleCurrent = (leaseId: number) => {
        setSelectedCurrent((current) =>
            current.includes(leaseId)
                ? current.filter((id) => id !== leaseId)
                : [...current, leaseId],
        )
    }

    const selectAllCurrent = () => {
        if (!data) return
        setSelectedCurrent(
            data.rows
                .filter((row) => isCurrentLease(row))
                .map((row) => row.lease_id as number),
        )
    }

    const runConfirm = () => {
        if (!confirm) return
        if (confirm.kind === 'seed' && datesInvalid) {
            toast.error(datesInvalid)
            return
        }
        if (confirm.kind === 'residue') residueMutation.mutate()
        if (confirm.kind === 'seed') seedMutation.mutate(selectedAreas)
        if (confirm.kind === 'season') seasonMutation.mutate(confirm.season.id)
    }

    const runManage = () => {
        if (manageAction !== 'delete' && datesInvalid) {
            toast.error(datesInvalid)
            return
        }
        if (currentCount === 0) return
        if (manageAction === 'dates') datesMutation.mutate(selectedCurrent)
        if (manageAction === 'delete')
            deleteCurrentMutation.mutate(selectedCurrent)
        if (manageAction === 'replace') replaceMutation.mutate(selectedCurrent)
    }

    if (query.isLoading) {
        return (
            <Box className={classes.root}>
                <Typography color="textSecondary">
                    Loading Demo Data…
                </Typography>
            </Box>
        )
    }

    if (query.isError || !data) {
        return (
            <Box className={classes.root}>
                <Typography variant="h5">Demo Data</Typography>
                <Typography color="textSecondary">
                    Demo Data is only available to EZOS staff.
                </Typography>
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.titleRow}>
                <Box className={classes.titleIcon}>
                    <StorageIcon fontSize="small" />
                </Box>
                <Box>
                    <Typography variant="h5" component="h1">
                        Demo Data
                    </Typography>
                    <Typography className={classes.subtitle}>
                        Lori U leases ({data.apartment_name}). The term below is
                        used when you seed empty beds or change current leases.
                        Sign in on the tenant site with the email on the lease.
                    </Typography>
                </Box>
            </Box>

            <Box className={classes.passwordRow}>
                <Typography variant="body2">
                    Demo password:{' '}
                    <span className={classes.passwordValue}>
                        {data.demo_password}
                    </span>
                </Typography>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                        navigator.clipboard.writeText(data.demo_password)
                        toast.success('Password copied.')
                    }}
                >
                    Copy password
                </Button>
            </Box>

            <Box className={classes.actions}>
                <Button
                    variant="outlined"
                    color="secondary"
                    disabled={busy || data.renewal_residue.parents === 0}
                    onClick={() => setConfirm({ kind: 'residue' })}
                >
                    Remove renewal residue ({data.renewal_residue.parents})
                </Button>
                <TextField
                    size="small"
                    variant="outlined"
                    label="Start"
                    type="date"
                    value={termStart}
                    onChange={(event) => setTermStart(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    size="small"
                    variant="outlined"
                    label="End"
                    type="date"
                    value={termEnd}
                    onChange={(event) => setTermEnd(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    disabled={busy || selectedCount === 0}
                    onClick={() => setConfirm({ kind: 'seed' })}
                >
                    Seed current leases ({selectedCount})
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    disabled={busy}
                    onClick={() => {
                        setManageAction(null)
                        setManageOpen(true)
                    }}
                >
                    Manage current leases ({currentCount})
                </Button>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search unit, bed, name, email"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
            </Box>

            <Box className={classes.seasons}>
                <Typography variant="subtitle2">Leasing seasons</Typography>
                {data.seasons.length === 0 ? (
                    <Typography className={classes.subtitle}>
                        No leasing seasons on Lori U.
                    </Typography>
                ) : (
                    data.seasons.map((season) => (
                        <Box key={season.id} className={classes.seasonRow}>
                            <Typography variant="body2">
                                {season.name}
                                {season.current ? ' · current' : ''} ·{' '}
                                {season.start} to {season.end} ·{' '}
                                {season.offer_count} offers ·{' '}
                                {season.lead_count} leads
                            </Typography>
                            <Button
                                size="small"
                                color="secondary"
                                disabled={busy}
                                onClick={() =>
                                    setConfirm({ kind: 'season', season })
                                }
                            >
                                Delete season
                            </Button>
                        </Box>
                    ))
                )}
            </Box>

            <Box className={classes.tableWrap}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                className={classes.headerCell}
                                padding="checkbox"
                            />
                            <TableCell className={classes.headerCell}>
                                Unit
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Bed
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Folder
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Name
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Email
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Status
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Start
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                End
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Renewal
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRows.map((row) => {
                            const leaseId = row.lease_id
                            return (
                                <TableRow
                                    key={
                                        leaseId == null
                                            ? `empty-${row.area_id}`
                                            : `lease-${leaseId}`
                                    }
                                >
                                    <TableCell padding="checkbox">
                                        {row.empty ? (
                                            <Checkbox
                                                color="primary"
                                                checked={selectedAreas.includes(
                                                    row.area_id,
                                                )}
                                                onChange={() =>
                                                    toggleArea(row.area_id)
                                                }
                                            />
                                        ) : isCurrentLease(row) &&
                                          leaseId != null ? (
                                            <Checkbox
                                                color="primary"
                                                checked={selectedCurrent.includes(
                                                    leaseId,
                                                )}
                                                onChange={() =>
                                                    toggleCurrent(leaseId)
                                                }
                                            />
                                        ) : null}
                                    </TableCell>
                                    <TableCell>{row.unit}</TableCell>
                                    <TableCell>{row.bed}</TableCell>
                                    <TableCell>{row.folder}</TableCell>
                                    <TableCell>{row.name || '—'}</TableCell>
                                    <TableCell>{row.email || '—'}</TableCell>
                                    <TableCell>
                                        {row.status || 'Empty'}
                                    </TableCell>
                                    <TableCell>{row.start || '—'}</TableCell>
                                    <TableCell>{row.end || '—'}</TableCell>
                                    <TableCell>
                                        {row.empty
                                            ? '—'
                                            : row.is_renewal
                                            ? 'Yes'
                                            : 'No'}
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </Box>

            <Dialog
                open={confirm != null}
                onClose={() => !busy && setConfirm(null)}
            >
                <DialogTitle>
                    {confirm?.kind === 'residue' && 'Remove renewal residue'}
                    {confirm?.kind === 'seed' && 'Seed current leases'}
                    {confirm?.kind === 'season' && 'Delete leasing season'}
                </DialogTitle>
                <DialogContent>
                    {confirm?.kind === 'residue' && (
                        <Typography>
                            Delete {data.renewal_residue.parents} pending
                            renewal leases and {data.renewal_residue.children}{' '}
                            com/bath child leases. Current and past leases stay.
                        </Typography>
                    )}
                    {confirm?.kind === 'seed' && (
                        <Typography>
                            Create {selectedCount} current leases from{' '}
                            {termStart} to {termEnd}. Each bed gets a resident
                            at leasingpipeline+areaId@ezos.co and com/bath
                            children when that bed has those mappings. Sign in
                            with the password on this page.
                        </Typography>
                    )}
                    {confirm?.kind === 'season' && (
                        <Typography>
                            Delete {confirm.season.name}. This removes{' '}
                            {confirm.season.offer_count} offers and{' '}
                            {confirm.season.lead_count} leads. Leases stay.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={runConfirm}
                        disabled={busy}
                    >
                        {busy ? 'Working…' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={manageOpen}
                onClose={() => !busy && closeManage()}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Manage current leases</DialogTitle>
                <DialogContent>
                    <Typography>
                        {currentCount} current leases selected. Term{' '}
                        {termStart || '—'} to {termEnd || '—'}.
                    </Typography>
                    {datesInvalid && (
                        <Typography color="error" variant="body2">
                            {datesInvalid}
                        </Typography>
                    )}
                    <Box className={classes.manageChoices} mt={1.5}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={selectAllCurrent}
                            disabled={busy}
                        >
                            Select all current
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setSelectedCurrent([])}
                            disabled={busy || currentCount === 0}
                        >
                            Clear
                        </Button>
                    </Box>
                    {manageAction == null ? (
                        <Box className={classes.manageOptions}>
                            <Box className={classes.manageOption}>
                                <Box className={classes.manageOptionText}>
                                    <Typography variant="subtitle2">
                                        Set dates
                                    </Typography>
                                    <Typography className={classes.subtitle}>
                                        Keeps these residents. Moves the lease
                                        term to the dates above. Status stays
                                        Current.
                                    </Typography>
                                </Box>
                                <Button
                                    className={classes.manageOptionButton}
                                    variant="outlined"
                                    onClick={() => setManageAction('dates')}
                                    disabled={
                                        busy ||
                                        currentCount === 0 ||
                                        Boolean(datesInvalid)
                                    }
                                >
                                    Set dates
                                </Button>
                            </Box>
                            <Box className={classes.manageOption}>
                                <Box className={classes.manageOptionText}>
                                    <Typography variant="subtitle2">
                                        Delete
                                    </Typography>
                                    <Typography className={classes.subtitle}>
                                        Removes these current leases, their
                                        com/bath children, and renewal offers on
                                        them.
                                    </Typography>
                                </Box>
                                <Button
                                    className={classes.manageOptionButton}
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => setManageAction('delete')}
                                    disabled={busy || currentCount === 0}
                                >
                                    Delete
                                </Button>
                            </Box>
                            <Box className={classes.manageOption}>
                                <Box className={classes.manageOptionText}>
                                    <Typography variant="subtitle2">
                                        Replace
                                    </Typography>
                                    <Typography className={classes.subtitle}>
                                        Deletes them, then creates new current
                                        leases on beds that are empty afterward.
                                        Uses the dates above and the demo
                                        password.
                                    </Typography>
                                </Box>
                                <Button
                                    className={classes.manageOptionButton}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setManageAction('replace')}
                                    disabled={
                                        busy ||
                                        currentCount === 0 ||
                                        Boolean(datesInvalid)
                                    }
                                >
                                    Replace
                                </Button>
                            </Box>
                            <Typography className={classes.subtitle}>
                                A past or pending lease on the same bed stays.
                                That bed is named after the action.
                            </Typography>
                        </Box>
                    ) : (
                        <Box className={classes.manageOptions}>
                            {manageAction === 'dates' && (
                                <Typography>
                                    Update {currentCount} current leases and
                                    their com/bath children to {termStart}{' '}
                                    through {termEnd}. Residents stay. Existing
                                    offers keep their own proposed dates.
                                </Typography>
                            )}
                            {manageAction === 'delete' && (
                                <Typography>
                                    Delete {currentCount} current leases and
                                    their com/bath children. Renewal offers on
                                    those leases are removed.
                                </Typography>
                            )}
                            {manageAction === 'replace' && (
                                <Typography>
                                    Delete {currentCount} current leases, then
                                    create new ones from {termStart} to{' '}
                                    {termEnd} on beds that are empty afterward.
                                    New residents use
                                    leasingpipeline+areaId@ezos.co and the
                                    password on this page.
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeManage} disabled={busy}>
                        Cancel
                    </Button>
                    {manageAction != null && (
                        <Button
                            onClick={() => setManageAction(null)}
                            disabled={busy}
                        >
                            Back
                        </Button>
                    )}
                    {manageAction != null && (
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={runManage}
                            disabled={busy}
                        >
                            {busy ? 'Working…' : 'Confirm'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    )
}
